import { DateOnly } from '@microsoft/kiota-abstractions';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { FilterModel, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import type {
  AgGridClientQuery,
  AllotmentReport,
  AllotmentReportCreateRequest, AllotmentReportDetailAmount,
  PagingResponseOfAllotmentReport,
} from '@/api/client/models/index.ts';
import type { DatasourceResponse } from '@/models/DatasourceResponse.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { useCurrentUserStore } from '@/stores/current-user-store.ts';
import { AllotmentRolesConstants as Roles, dateFormatter } from '@/stores/definitions.js';
import { mapFilterModelToApi, mapSortModelToApi } from '@/util/grid-to-api-mappers.ts';

type ReportListRow = Omit<AllotmentReport, 'created' | 'fromDate' | 'toDate'> & {
  created?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
};

function toNumberOrNull(value: unknown): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
}

function toDateOnlyOrNull(value: unknown): DateOnly | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return null;
  }

  if (value instanceof DateOnly) {
    return value;
  }

  if (value instanceof Date) {
    return DateOnly.fromDate(value);
  }

  if (typeof value === 'string') {
    return DateOnly.parse(value) ?? null;
  }

  return undefined;
}

function toDateOnlyString(value: { toString(): string } | null | undefined): string | null {
  return value ? value.toString() : null;
}

function normalizeListRows(rows: AllotmentReport[] | null | undefined): ReportListRow[] {
  return (rows ?? []).map(entry => ({
    ...entry,
    created: entry.created ? dateFormatter(entry.created).date : null,
    fromDate: toDateOnlyString(entry.fromDate),
    toDate: toDateOnlyString(entry.toDate),
  }));
}

function buildCreateRequest(data: Record<string, unknown>): AllotmentReportCreateRequest {
  return {
    ...data,
    day: toNumberOrNull(data.day),
    endDate: toDateOnlyOrNull(data.endDate),
    month: toNumberOrNull(data.month),
    netFare: toNumberOrNull(data.netFare),
    seasonId: toNumberOrNull(data.seasonId),
    size: toNumberOrNull(data.size),
    startDate: toDateOnlyOrNull(data.startDate),
    week: toNumberOrNull(data.week),
    yq: toNumberOrNull(data.yq),
  } as AllotmentReportCreateRequest;
}

export const useReportsStore = defineStore('reports', () => {
  const apiClient = useApiClient();
  const currentUserStore = useCurrentUserStore();

  const loading = ref<boolean>(false);
  const fieldMap: Record<string, string> = {};

  async function resolveRole(explicitRole?: number): Promise<number | undefined> {
    if (explicitRole !== undefined && explicitRole !== null) {
      return explicitRole;
    }

    if (currentUserStore.currentUser?.allotmentRoleId !== undefined && currentUserStore.currentUser?.allotmentRoleId !== null) {
      return Number(currentUserStore.currentUser.allotmentRoleId);
    }

    const currentUser = await currentUserStore.get();
    return currentUser?.allotmentRoleId ?? undefined;
  }

  async function get(request: IServerSideGetRowsRequest): Promise<DatasourceResponse<ReportListRow>> {
    loading.value = true;

    const query: AgGridClientQuery = {
      startRow: request.startRow,
      endRow: request.endRow,
      sortModel: mapSortModelToApi(request.sortModel, fieldMap),
      filterModel: mapFilterModelToApi((request.filterModel ?? {}) as FilterModel, fieldMap),
    };

    try {
      const role = await resolveRole();
      const response: PagingResponseOfAllotmentReport | undefined = role === Roles.TOUR_OPERATOR
        ? await apiClient.allotmentReport.getForTourOperator.post(query)
        : await apiClient.allotmentReport.getPath.post(query);

      return {
        total: response?.total ?? 0,
        rows: normalizeListRows(response?.rows),
      };
    } finally {
      loading.value = false;
    }
  }

  async function request(data: Record<string, unknown>, role?: number): Promise<void> {
    const resolvedRole = await resolveRole(role);
    const requestBody = buildCreateRequest(data);

    if (resolvedRole === Roles.TOUR_OPERATOR) {
      await apiClient.allotmentReport.createForTourOperator.post(requestBody);
      return;
    }

    await apiClient.allotmentReport.create.post(requestBody);
  }

  async function getById({ id }: { id?: number } = {}): Promise<AllotmentReportDetailAmount[]> {
    if (id == null) {
      throw new Error('Report ID is required to load report details.');
    }

    loading.value = true;

    try {
      const response = await apiClient.allotmentReport.byRequestId(id).get();
      if (!response) {
        throw new Error('Report details request returned no response.');
      }
      return response;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    get,
    request,
    getById,
  };
});
